import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuPlus, LuPencil, LuTrash2, LuArrowUp, LuArrowDown, LuCheck, LuMonitorPlay, LuFileDown, LuFolder,
} from 'react-icons/lu';
import RichTextEditor from './RichTextEditor';
import { LESSON_TYPES, Modal, errorMessage, readFileAsDataUrl } from './trainingUtils';

const MAX_MATERIAL_MB = 10;

function ModuleModal({ courseId, editing, onClose, onSaved }) {
  const [form, setForm] = useState({ title: editing?.title || '', description: editing?.description || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Informe o título do módulo.'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await api.put(`/training/modules/${editing.id}`, form);
      else await api.post(`/training/courses/${courseId}/modules`, form);
      onSaved();
    } catch (e) {
      setError(errorMessage(e)); setSaving(false);
    }
  };

  return (
    <Modal title={editing ? 'Editar módulo' : 'Novo módulo'} icon={<LuFolder color="var(--accent)" />} onClose={onClose} maxWidth={520}>
      {error && <div className="alert-custom danger">{error}</div>}
      <div>
        <label className="form-label">Título *</label>
        <input className="form-control" value={form.title} autoFocus maxLength={200} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </div>
      <div>
        <label className="form-label">Descrição</label>
        <textarea className="form-control" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      <div className="training-modal-footer">
        <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}><LuCheck /> {saving ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </Modal>
  );
}

function LessonModal({ moduleId, modules, editingId, onClose, onSaved }) {
  const [form, setForm] = useState(() => (editingId ? null : {
    title: '', required: true, content_type: 'text', video_url: '', video_duration_minutes: '', content_html: '', material_name: '', material_url: '', module_id: moduleId,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Edição: busca a aula completa (a árvore do curso não traz o arquivo do material).
  useEffect(() => {
    if (!editingId) return undefined;
    let cancelled = false;
    api.get(`/training/lessons/${editingId}`)
      .then(({ data }) => !cancelled && setForm({
        title: data.title,
        required: data.required,
        content_type: data.content_type,
        video_url: data.video_url || '',
        video_duration_minutes: data.video_duration_minutes ?? '',
        content_html: data.content_html || '',
        material_name: data.material_name || '',
        material_url: data.material_url || '',
        module_id: data.module_id,
      }))
      .catch((e) => !cancelled && setError(errorMessage(e, 'Erro ao carregar a aula')));
    return () => { cancelled = true; };
  }, [editingId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleMaterial = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_MATERIAL_MB * 1024 * 1024) { setError(`O material deve ter no máximo ${MAX_MATERIAL_MB} MB.`); return; }
    setForm((f) => ({ ...f, material_name: file.name }));
    set('material_url', await readFileAsDataUrl(file));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Informe o título da aula.'); return; }
    setSaving(true); setError('');
    const payload = {
      ...form,
      video_duration_minutes: form.video_duration_minutes === '' ? null : Number(form.video_duration_minutes),
      material_url: form.material_url || null,
      material_name: form.material_url ? (form.material_name || 'material') : null,
    };
    try {
      if (editingId) await api.put(`/training/lessons/${editingId}`, payload);
      else await api.post(`/training/modules/${moduleId}/lessons`, payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e)); setSaving(false);
    }
  };

  const hasVideo = form && ['video', 'video_text'].includes(form.content_type);
  const hasText = form && ['text', 'video_text'].includes(form.content_type);

  return (
    <Modal title={editingId ? 'Editar aula' : 'Nova aula'} icon={<LuMonitorPlay color="var(--accent)" />} onClose={onClose} maxWidth={820}>
      {error && <div className="alert-custom danger">{error}</div>}
      {!form ? <div className="training-hint">Carregando…</div> : (
        <>
          <div className="training-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Título *</label>
              <input className="form-control" value={form.title} autoFocus maxLength={200} onChange={(e) => set('title', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Tipo</label>
              <select className="form-control" value={form.content_type} onChange={(e) => set('content_type', e.target.value)}>
                {Object.entries(LESSON_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {editingId && (
              <div>
                <label className="form-label">Módulo</label>
                <select className="form-control" value={form.module_id} onChange={(e) => set('module_id', Number(e.target.value))}>
                  {modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
              </div>
            )}
          </div>
          <label className="training-check">
            <input type="checkbox" checked={form.required} onChange={(e) => set('required', e.target.checked)} />
            Aula obrigatória (conta para a aprovação no curso)
          </label>

          {hasVideo && (
            <div className="training-form-grid">
              <div style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Link do vídeo *</label>
                <input className="form-control" value={form.video_url} placeholder="https://www.youtube.com/watch?v=… ou https://vimeo.com/…"
                  onChange={(e) => set('video_url', e.target.value)} />
              </div>
              <div>
                <label className="form-label">Duração (minutos)</label>
                <input className="form-control" type="number" min="0" value={form.video_duration_minutes} onChange={(e) => set('video_duration_minutes', e.target.value)} />
              </div>
            </div>
          )}

          {hasText && (
            <div>
              <label className="form-label">Conteúdo {form.content_type === 'text' ? '*' : ''}</label>
              <RichTextEditor value={form.content_html} onChange={(html) => set('content_html', html)} />
            </div>
          )}

          <div>
            <label className="form-label">Material de apoio (opcional)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {form.material_url ? (
                <>
                  <span style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}><LuFileDown /> {form.material_name || 'material'}</span>
                  <button type="button" className="training-btn training-btn--sm training-btn--danger" onClick={() => setForm((f) => ({ ...f, material_url: '', material_name: '' }))}>
                    <LuTrash2 /> Remover
                  </button>
                </>
              ) : (
                <label className="training-btn training-btn--sm" style={{ marginBottom: 0 }}>
                  <LuFileDown /> Anexar arquivo
                  <input type="file" hidden onChange={handleMaterial} />
                </label>
              )}
            </div>
            <div className="training-hint">PDF, planilha, apresentação etc. — até {MAX_MATERIAL_MB} MB.</div>
          </div>

          <div className="training-modal-footer">
            <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
            <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}><LuCheck /> {saving ? 'Salvando…' : 'Salvar aula'}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default function TrainingContentTab({ course, onChanged }) {
  const [moduleModal, setModuleModal] = useState(null); // 'new' | módulo
  const [lessonModal, setLessonModal] = useState(null); // { moduleId, editingId }
  const [error, setError] = useState('');
  const locked = course.has_students;

  const run = async (fn) => {
    setError('');
    try { await fn(); onChanged(); } catch (e) { setError(errorMessage(e, 'Não foi possível concluir a ação')); }
  };

  const move = (list, index, dir) => {
    const ids = list.map((x) => x.id);
    const target = index + dir;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    return ids;
  };

  const handleMoveModule = (index, dir) => run(() => api.put(`/training/courses/${course.id}/modules/reorder`, { ids: move(course.modules, index, dir) }));
  const handleMoveLesson = (mod, index, dir) => run(() => api.put(`/training/modules/${mod.id}/lessons/reorder`, { ids: move(mod.lessons, index, dir) }));
  const handleDeleteModule = (m) => {
    if (!window.confirm(`Excluir o módulo "${m.title}" com todas as aulas e avaliações dele?`)) return;
    run(() => api.delete(`/training/modules/${m.id}`));
  };
  const handleDeleteLesson = (l) => {
    if (!window.confirm(`Excluir a aula "${l.title}"?`)) return;
    run(() => api.delete(`/training/lessons/${l.id}`));
  };

  const lockTitle = locked ? 'Curso com alunos: o conteúdo não pode ser excluído' : undefined;

  return (
    <div>
      {error && <div className="alert-custom danger">{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div className="training-hint" style={{ marginTop: 0 }}>Organize o curso em módulos e aulas. Use as setas para reordenar.</div>
        <button type="button" className="training-btn training-btn--primary" onClick={() => setModuleModal('new')}><LuPlus /> Novo módulo</button>
      </div>

      {!course.modules.length && (
        <div className="empty-state"><LuFolder size={32} color="var(--text-3)" /><p>Nenhum módulo ainda</p><small>Comece criando o primeiro módulo do curso.</small></div>
      )}

      {course.modules.map((m, mi) => (
        <div key={m.id} className="training-block">
          <div className="training-block-header">
            <div className="training-block-title">
              <LuFolder color="var(--accent)" /> Módulo {mi + 1}: {m.title}
              <span className="training-block-sub">{m.lessons.length} aula{m.lessons.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="training-row-actions">
              <button type="button" className="training-icon-btn" title="Subir" disabled={mi === 0} onClick={() => handleMoveModule(mi, -1)}><LuArrowUp /></button>
              <button type="button" className="training-icon-btn" title="Descer" disabled={mi === course.modules.length - 1} onClick={() => handleMoveModule(mi, 1)}><LuArrowDown /></button>
              <button type="button" className="training-icon-btn" title="Editar módulo" onClick={() => setModuleModal(m)}><LuPencil /></button>
              <button type="button" className="training-icon-btn training-icon-btn--danger" title={lockTitle || 'Excluir módulo'} disabled={locked} onClick={() => handleDeleteModule(m)}><LuTrash2 /></button>
            </div>
          </div>
          {m.description && <div className="training-row" style={{ color: 'var(--text-2)' }}>{m.description}</div>}
          {m.lessons.map((l, li) => (
            <div key={l.id} className="training-row">
              <div className="training-row-main">
                <div className="training-row-title">{li + 1}. {l.title}</div>
                <div className="training-row-meta">
                  <span>{LESSON_TYPES[l.content_type]}</span>
                  <span>{l.required ? 'Obrigatória' : 'Opcional'}</span>
                  {l.video_duration_minutes ? <span>{l.video_duration_minutes} min</span> : null}
                  {l.has_material && <span><LuFileDown /> {l.material_name}</span>}
                </div>
              </div>
              <div className="training-row-actions">
                <button type="button" className="training-icon-btn" title="Subir" disabled={li === 0} onClick={() => handleMoveLesson(m, li, -1)}><LuArrowUp /></button>
                <button type="button" className="training-icon-btn" title="Descer" disabled={li === m.lessons.length - 1} onClick={() => handleMoveLesson(m, li, 1)}><LuArrowDown /></button>
                <button type="button" className="training-icon-btn" title="Editar aula" onClick={() => setLessonModal({ moduleId: m.id, editingId: l.id })}><LuPencil /></button>
                <button type="button" className="training-icon-btn training-icon-btn--danger" title={lockTitle || 'Excluir aula'} disabled={locked} onClick={() => handleDeleteLesson(l)}><LuTrash2 /></button>
              </div>
            </div>
          ))}
          <div className="training-row">
            <button type="button" className="training-btn training-btn--sm" onClick={() => setLessonModal({ moduleId: m.id, editingId: null })}><LuPlus /> Nova aula</button>
          </div>
        </div>
      ))}

      {moduleModal && (
        <ModuleModal courseId={course.id} editing={moduleModal === 'new' ? null : moduleModal}
          onClose={() => setModuleModal(null)} onSaved={() => { setModuleModal(null); onChanged(); }} />
      )}
      {lessonModal && (
        <LessonModal moduleId={lessonModal.moduleId} editingId={lessonModal.editingId} modules={course.modules}
          onClose={() => setLessonModal(null)} onSaved={() => { setLessonModal(null); onChanged(); }} />
      )}
    </div>
  );
}
